/* @flow */

import React, { type Node, Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import { DDAText } from "./steps/DDAText";
import DDAFeeAccountInformationForm from "./steps/DDAFeeAccountInformationForm";
import { createEmptyDDAEntry } from "./DDAFeeAccountHelper";
import type {
  RootState,
  UIState,
  DDAEntry,
} from "../../types";
import Button from "../Button";
import styles from "../Modal.scss";

type Step = {
  previous: string | null,
  renderFn: () => Node,
  next: () => Promise<string>
};

type AddEditDDAEntryProps = {
  commitDDAEntry: (ddaEntry: DDAEntry) => void,
  seedDDAEntry: DDAEntry | null,
  ui: UIState,
  onClose?: () => void
};

type AddEditDDAEntryState = {
  currentStep: string,
  ddaEntry: DDAEntry
};

export class AddEditDDAEntry extends PureComponent<
  AddEditDDAEntryProps,
  AddEditDDAEntryState
> {
  steps: { [string]: Step };

  constructor(props: AddEditDDAEntryProps) {
    super(props);

    this.steps = {
      DDA_TEXT: {
        previous: null,
        renderFn: () => (
          <div>
            <DDAText onComplete={this.handleDdaTextContinue} initialPage={this.props.__testProps?.initialPage || 0} />
          </div>
        ),
        next: () => Promise.resolve("FEE_ACCOUNT_INFORMATION")
      },
      FEE_ACCOUNT_INFORMATION: {
        previous: "DDA_TEXT",
        renderFn: () => (
          <div>
            <DDAFeeAccountInformationForm
              onSuccess={this.handleFeeAccountInfoSubmit}
              ddaEntry={this.state.ddaEntry}
              ui={this.props.ui}
            />
          </div>
        ),
        next: () => Promise.resolve(null)
      }
    };
    this.state = {
      currentStep: "DDA_TEXT",
      ddaEntry: props.seedDDAEntry || createEmptyDDAEntry()
    };
  }

  advance = async () => {
    const { currentStep } = this.state;
    const nextStep = await this.steps[currentStep].next();
    if (nextStep && this.steps[nextStep]) {
      this.setState({ currentStep: nextStep });
    }
  };

  handlePreviousButtonClick = (event: SyntheticEvent<>) => {
    event.preventDefault();
    const previousStep = this.steps[this.state.currentStep].previous;
    if (previousStep !== null) {
      this.setState({ currentStep: previousStep });
    }
  };

  handleDdaTextContinue = () => {
    this.advance();
  };

  handleFeeAccountInfoSubmit = (values) => {
    this.setState(
      prevState => ({
        ddaEntry: { ...prevState.ddaEntry, ...values }
      }),
      () => {
        const { commitDDAEntry, onClose } = this.props;

        if (commitDDAEntry) {
          const entryWithId = {
            ...this.state.ddaEntry,
            id: this.state.ddaEntry.id
          };
          commitDDAEntry(entryWithId);
        }

        // Close the modal
        if (onClose) {
          onClose();
        }
      }
    );
  };

  render() {
    const ui = this.props.ui || {};
    const currentStep = this.steps[this.state.currentStep];
    const canNavigateToPrevious = currentStep.previous !== null;

    return (
      <Fragment>
        {canNavigateToPrevious && (
          <div className={styles.backButton}>
            <Button
              intent="simple"
              size="small"
              disabled={ui.asyncActionInFlight}
              onClick={this.handlePreviousButtonClick}>
              &lsaquo; Back
            </Button>
          </div>
        )}
        {currentStep.renderFn()}
      </Fragment>
    );
  }
}

const mapStateToProps = ({ ui }: RootState) => ({
  ui,
});

// $FlowFixMe
export default connect(mapStateToProps)(AddEditDDAEntry);